/**
 * TanStack Query hooks for the SubContractorHub sync errors view (SCH-5.2).
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { syncErrorsService } from '@/services/integrations/syncErrorsService';
import type {
  SyncError,
  SyncErrorsFilters,
} from '@/types/integrations/syncErrors';

const SYNC_ERRORS_KEY = ['sch', 'sync-errors'] as const;

export function useSyncErrors(filters: SyncErrorsFilters = {}) {
  return useQuery({
    queryKey: [
      ...SYNC_ERRORS_KEY,
      filters.acknowledged ?? 'false',
      filters.phase ?? 'all',
      filters.subhubProjectId ?? 'all',
      filters.startedAfter ?? null,
      filters.startedBefore ?? null,
      filters.limit ?? 25,
      filters.offset ?? 0,
    ],
    queryFn: () => syncErrorsService.listSyncErrors(filters),
    staleTime: 10_000,
  });
}

/**
 * Mutation: acknowledge a sync error. On success, the row's
 * acknowledgedAt / acknowledgedBy are set; the React Query cache for
 * sync-errors is invalidated so any active filter re-renders.
 */
export function useAcknowledgeSyncError() {
  const queryClient = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof syncErrorsService.acknowledgeSyncError>>,
    Error,
    string
  >({
    mutationFn: (errorId: string) =>
      syncErrorsService.acknowledgeSyncError(errorId),
    onSuccess: (result) => {
      if (result.data) {
        queryClient.invalidateQueries({ queryKey: SYNC_ERRORS_KEY });
      }
    },
    retry: false,
  });
}

export function useUnacknowledgeSyncError() {
  const queryClient = useQueryClient();
  return useMutation<
    Awaited<ReturnType<typeof syncErrorsService.unacknowledgeSyncError>>,
    Error,
    string
  >({
    mutationFn: (errorId: string) =>
      syncErrorsService.unacknowledgeSyncError(errorId),
    onSuccess: (result) => {
      if (result.data) {
        queryClient.invalidateQueries({ queryKey: SYNC_ERRORS_KEY });
      }
    },
    retry: false,
  });
}

export type { SyncError, SyncErrorsFilters };
